import { LICENSE_TYPE, paths } from '@common/types';
import {
  Box,
  TextField,
  FormControl,
  FormHelperText,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { Controller, Form } from 'react-hook-form';
import { useLicenses } from './hooks';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Select } from 'ui';
import { SaveChangesModal } from '@components';

interface ILicensesProps {
  user: paths[`/user/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  };
}

export const Licenses = ({ user }: ILicensesProps) => {
  const {
    control,
    errors,
    handleAddLicense,
    handleRemoveLicense,
    isSubmitEnabled,
    isSubmitting,
    licenseFields,
    onSubmit,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useLicenses(user);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Form
        control={control}
        onSubmit={e => onSubmit(e.event)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          height: 'fit-content',
          borderRadius: 0,
          backgroundColor: 'white',
          position: 'relative',
          gap: '1rem',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography
            variant='subtitle2'
            sx={{
              width: '100%',
              textAlign: 'left',
            }}
          >
            {t('adminPanel.users.addModal.licensesTitle')}
          </Typography>

          {!isNil(errors['licenses']) && (
            <FormHelperText
              error={!isNil(errors['licenses'])}
              sx={{ color: 'red', width: '100%' }}
            >
              {errors['licenses'] ? errors['licenses'].message : null}
            </FormHelperText>
          )}
          <Box sx={{ width: '100%' }}>
            <Button
              startIcon={<AddIcon />}
              sx={{
                fontWeight: 'regular',
                fontSize: '1rem',
                color: 'primary.main',
              }}
              color='secondary'
              variant='text'
              onClick={handleAddLicense}
            >
              {t('adminPanel.users.addModal.addLicense')}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            gap: '1rem',
            flexDirection: 'column',
          }}
        >
          {licenseFields.map((licenseEntry, index) => (
            <Box
              key={`licenseEntry-${licenseEntry.id}-${index}`}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: '1rem',
                  flexDirection: 'horizontal',
                }}
              >
                <Controller
                  name={`licenses.${index}.type`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      size='medium'
                      label={`${t('adminPanel.users.addModal.licenseTypeLabel')} ${index + 1}`}
                      error={!isNil(error)}
                      required
                      options={Object.values(LICENSE_TYPE).map(o => ({
                        value: o,
                        label: o,
                      }))}
                      helperText={error?.message}
                    />
                  )}
                />

                <Controller
                  name={`licenses.${index}.id`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth>
                      <TextField
                        {...field}
                        type='text'
                        size='medium'
                        label={t('adminPanel.users.addModal.licenseIdLabel')}
                        variant='outlined'
                        error={!isNil(error)}
                        helperText={error ? error.message : null}
                        fullWidth
                      />
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Button
                  startIcon={<DeleteIcon />}
                  sx={{
                    fontWeight: 'regular',
                    fontSize: '0.75rem',
                    color: 'error',
                    mt: '0.25rem',
                  }}
                  size='small'
                  color='error'
                  variant='text'
                  onClick={() => handleRemoveLicense(index)}
                >
                  {t('adminPanel.users.addModal.removeLicense')}
                </Button>
              </Box>

              {licenseFields.length !== index + 1 && (
                <Divider
                  orientation='horizontal'
                  sx={{
                    width: '100%',
                    marginTop: '0.5rem',
                    marginBottom: '0.25rem',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            pt: '0.5rem',
            position: 'sticky',
            bottom: '0',
            left: 0,
            bgcolor: theme => theme.palette.common.white,
            zIndex: 10,
          }}
        >
          <Button
            type='submit'
            variant='contained'
            color='secondary'
            sx={{
              fontSize: '0,875rem',
              fontWeight: 'medium',
              lineHeight: '24px',
              color: 'white',
            }}
            disabled={!isSubmitEnabled}
            loading={isSubmitting}
            loadingPosition='center'
          >
            {t('general.saveChanges')}
          </Button>
        </Box>
        <SaveChangesModal
          isOpen={isSaveChangesModalOpen}
          onClose={handleCloseSaveChangesModal}
          isValid={isValid}
          handleSaveChanges={handleSaveChanges}
          discardChanges={handleDiscardChanges}
          isLoading={isEditSubmitting}
        />
      </Form>
    </Box>
  );
};
